import { useQuery } from '@tanstack/react-query';
import oaiApiClient from '../clients/oai-api/oai-api-client';
import { queryClient } from '../query-client';
import { parseToken } from '@oai-frontend-monorepo/utils';
import gatewayApiClient from '../clients/gateway-api/gateway-api.client';
import { AccountBalance } from '@oai-frontend-monorepo/types';
import { ensurePositionsTotalPnl } from '../private-utils/calculateOpenPnl';
import { useEffect, useState } from 'react';

export const useBalance = () => {
  const query = useQuery({
    queryKey: ['balance'],
    queryFn: async () => {
      const token = await oaiApiClient.client.token?.();
      const parsed = parseToken(token);

      const isSubscriber = !parsed?.groups?.includes('Traders');

      if (isSubscriber) {
        const paperDepProm = oaiApiClient.getPaperDeposit();
        const paperPnlProm = gatewayApiClient.getPpaperPnl();
        const totalPnlProm = ensurePositionsTotalPnl();

        const [paperDep, paperPnl, openPnl] = await Promise.all([
          paperDepProm,
          paperPnlProm,
          totalPnlProm,
        ]);

        if (openPnl === undefined) {
          return null;
        }

        return {
          paper: {
            deposit: paperDep.deposit,
          },
          balances: {
            account_type: 'margin',
            total_equity:
              (paperDep.deposit || 0) +
              (paperPnl?.profit || 0) +
              (openPnl?.pnl || 0),
            margin: {
              option_buying_power:
                (paperDep.deposit || 0) +
                (paperPnl?.profit || 0) -
                (openPnl?.maxLoss || 0),
            },
          },
        } as AccountBalance;
      }

      const isTrader = parsed?.groups?.includes('Traders');

      if (!isTrader) {
        return null;
      }

      return oaiApiClient.getBalance() || null;
    },
    refetchInterval: 5000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const [lastError, setLastError] = useState<Error | null>(null);

  useEffect(() => {
    if (query.error) {
      setLastError(query.error);
    } else if (query.isSuccess) {
      setLastError(null);
    }
  }, [query.error, query.isSuccess]);

  return {
    ...query,
    lastError,
  };
};

export const prefetchBalance = async () => {
  return await queryClient.prefetchQuery({
    queryKey: ['balance'],
    queryFn: async () => {
      const token = await oaiApiClient.client.token?.();
      const parsed = parseToken(token);

      const isSubscriber = !parsed?.groups?.includes('Traders');

      if (isSubscriber) {
        const paperDepProm = oaiApiClient.getPaperDeposit();
        const paperPnlProm = gatewayApiClient.getPpaperPnl();
        const totalPnlProm = ensurePositionsTotalPnl();

        const [paperDep, paperPnl, openPnl] = await Promise.all([
          paperDepProm,
          paperPnlProm,
          totalPnlProm,
        ]);

        if (openPnl === undefined) {
          return null;
        }

        return {
          paper: {
            deposit: paperDep.deposit,
          },
          balances: {
            account_type: 'margin',
            total_equity:
              (paperDep.deposit || 0) +
              (paperPnl?.profit || 0) +
              (openPnl?.pnl || 0),
            margin: {
              option_buying_power:
                (paperDep.deposit || 0) +
                (paperPnl?.profit || 0) -
                (openPnl?.maxLoss || 0),
            },
          },
        } as AccountBalance;
      }

      const isTrader = parsed?.groups?.includes('Traders');

      if (!isTrader) {
        return null;
      }

      return oaiApiClient.getBalance() || null;
    },
  });
};

export const ensureBalance = async () => {
  return await queryClient.ensureQueryData({
    queryKey: ['balance'],
    queryFn: async () => {
      const token = await oaiApiClient.client.token?.();
      const parsed = parseToken(token);

      const isSubscriber = !parsed?.groups?.includes('Traders');

      if (isSubscriber) {
        const paperDepProm = oaiApiClient.getPaperDeposit();
        const paperPnlProm = gatewayApiClient.getPpaperPnl();
        const totalPnlProm = ensurePositionsTotalPnl();

        const [paperDep, paperPnl, openPnl] = await Promise.all([
          paperDepProm,
          paperPnlProm,
          totalPnlProm,
        ]);

        if (openPnl === undefined) {
          return null;
        }

        return {
          paper: {
            deposit: paperDep.deposit,
          },
          balances: {
            account_type: 'margin',
            total_equity:
              (paperDep.deposit || 0) +
              (paperPnl?.profit || 0) +
              (openPnl?.pnl || 0),
            margin: {
              option_buying_power:
                (paperDep.deposit || 0) +
                (paperPnl?.profit || 0) -
                (openPnl?.maxLoss || 0),
            },
          },
        } as AccountBalance;
      }

      const isTrader = parsed?.groups?.includes('Traders');

      if (!isTrader) {
        return null;
      }

      return oaiApiClient.getBalance() || null;
    },
    retry: 0,
  });
};
